import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import PrimaryNavigation from '../../components/ui/PrimaryNavigation';
import ContextualBreadcrumb from '../../components/ui/ContextualBreadcrumb';
import FloatingActionButton from '../../components/ui/FloatingActionButton';
import PhaseHeader from './components/PhaseHeader';
import TimelineComparison from './components/TimelineComparison';
import PhaseActions from './components/PhaseActions';
import ProjectTimeline from './components/ProjectTimeline';
import AIPhaseManagement from './components/AIPhaseManagement';
import { phaseService } from '../../services/phaseService';
import { projectService } from '../../services/projectService';
import { realtimeService } from '../../services/realtimeService';
import { supabase } from '../../lib/supabase';
import Icon from '../../components/AppIcon';

const PhaseManagement = () => {

  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();

  const projectId =
    searchParams?.get('projectId') || location?.state?.projectId;

  const incomingPhaseId =
    searchParams?.get('phaseId') || location?.state?.phaseId;

  const projectName = location?.state?.projectName || '';

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const [project, setProject] = useState(null);
  const [phases, setPhases] = useState([]);
  const [allPhases, setAllPhases] = useState([]);

  const [phaseData, setPhaseData] = useState(null);

  const [projectLifecycleStatus, setProjectLifecycleStatus] =
    useState('dormant');

  const [remarks, setRemarks] = useState([]);

  const [local3M, setLocal3M] = useState({
    plumber: 0,
    electrician: 0,
    aircon: 0
  });

  const isThreeMusketeers =
    phaseData?.phase_name === '3 Musketeers' ||
    phaseData?.phase_name === '3 Musketeers Installation';

  const isAiProject = project?.source === 'ai_import';

  /* ---------------- LOAD DATA ---------------- */

  useEffect(() => {

    const loadData = async () => {

      if (!projectId) {
        setError('Project not found.');
        setLoading(false);
        return;
      }

      try {

        setLoading(true);

        const fetchedProject = await projectService?.getProjectById(projectId);
        setProject(fetchedProject);
        setProjectLifecycleStatus(fetchedProject?.lifecycle_status || 'dormant');

        const fetchedPhases =
          await phaseService?.getProjectPhases(projectId);

        setPhases(fetchedPhases);
        setAllPhases(fetchedPhases);

        // For AI projects we don't need to resolve a single active phase
        if (fetchedProject?.source !== 'ai_import') {
          let resolvedPhaseId = incomingPhaseId;

          if (!resolvedPhaseId) {
            const ongoing = fetchedPhases?.find(
              (p) => p?.progress > 0 && p?.progress < 100
            );
            resolvedPhaseId = ongoing?.id || fetchedPhases?.[0]?.id;
          }

          const active = fetchedPhases?.find(
            (p) => p?.id === resolvedPhaseId
          );

          setPhaseData(active);

          if (active?.id) {
            const phaseRemarks = await phaseService?.getPhaseRemarks(active?.id);
            setRemarks(phaseRemarks || []);
          }
        }

        setLoading(false);

      } catch (err) {

        console.error(err);
        setError('Failed to load phase.');
        setLoading(false);

      }

    };

    loadData();

  }, [projectId, incomingPhaseId]);

  /* ---------------- SYNC 3M STATE ---------------- */

  useEffect(() => {

    if (phaseData && isThreeMusketeers) {

      setLocal3M({
        plumber: phaseData?.plumber_progress || 0,
        electrician: phaseData?.electrician_progress || 0,
        aircon: phaseData?.aircon_progress || 0
      });

    }

  }, [phaseData]);

  /* ---------------- REALTIME ---------------- */

  useEffect(() => {

    if (!projectId) return;

    const handleUpdate = async (updatedPhase) => {

      setPhases((prev) =>
        prev?.map((p) =>
          p?.id === updatedPhase?.id ? updatedPhase : p
        )
      );

      if (phaseData?.id === updatedPhase?.id) {

        setPhaseData(updatedPhase);

        const phaseRemarks =
          await phaseService?.getPhaseRemarks(updatedPhase?.id);

        setRemarks(phaseRemarks || []);

      }

    };

    realtimeService?.subscribeToPhaseUpdates(projectId, handleUpdate);

    return () => {
      realtimeService?.unsubscribe(`phase-updates-${projectId}`);
    };

  }, [projectId, phaseData?.id]);

  /* ---------------- PROGRESS ---------------- */

  const handleProgressChange = async (value) => {
    await phaseService?.updatePhaseProgress(phaseData?.id, value);
  };

  /* ---------------- 3M ---------------- */

  const handle3MChange = (trade, value) => {
    setLocal3M(prev => ({ ...prev, [trade]: value }));
  };

  const commit3MProgress = async () => {

    const avg = Math.round(
      (local3M?.plumber +
        local3M?.electrician +
        local3M?.aircon) / 3
    );

    await supabase?.from('phases')?.update({
        plumber_progress: local3M?.plumber,
        electrician_progress: local3M?.electrician,
        aircon_progress: local3M?.aircon,
        progress: avg,
        updated_at: new Date()?.toISOString()
      })?.eq('id', phaseData?.id);

  };

  const computedAverage = Math.round(
    (local3M?.plumber +
      local3M?.electrician +
      local3M?.aircon) / 3
  );

  /* ---------------- ACTIONS ---------------- */

  const handleOverride = async (reason) => {

    if (!phaseData) return;

    const currentIndex = phases?.findIndex(
      (p) => p?.id === phaseData?.id
    );

    const isFinalPhase =
      currentIndex !== -1 &&
      currentIndex === phases?.length - 1;

    try {

      setLoading(true);

      if (projectLifecycleStatus === 'dormant') {

        const firstPhase = phases?.[0];

        await projectService?.updateProjectLifecycleStatus(
          projectId,
          'active'
        );

        await phaseService?.updatePhaseProgress(
          firstPhase?.id,
          1
        );

        if (reason) {
          await phaseService?.addPhaseRemark(
            firstPhase?.id,
            projectId,
            reason,
            'override'
          );
        }

        window.location.href =
          `/phase-management?projectId=${projectId}&phaseId=${firstPhase?.id}`;

        return;
      }

      if (isFinalPhase) {

        await phaseService?.updatePhaseProgress(
          phaseData?.id,
          100
        );

        await projectService?.updateProjectStatus(
          projectId,
          'completed'
        );

        await projectService?.updateProjectLifecycleStatus(
          projectId,
          'completed'
        );

        navigate('/boss-home-page');

        return;

      }

      const nextPhase = phases?.[currentIndex + 1];

      await phaseService?.updatePhaseProgress(
        phaseData?.id,
        100
      );

      await phaseService?.updatePhaseProgress(
        nextPhase?.id,
        1
      );

      window.location.href =
        `/phase-management?projectId=${projectId}&phaseId=${nextPhase?.id}`;

    } catch (err) {

      console.error(err);

    } finally {

      setLoading(false);

    }

  };

  const handleAdvancePhase = async () => {

    const index = phases?.findIndex(
      (p) => p?.id === phaseData?.id
    );

    if (index === -1) return;

    const next = phases?.[index + 1];

    if (!next) return;

    await phaseService?.updatePhaseProgress(phaseData?.id, 100);
    await phaseService?.updatePhaseProgress(next?.id, 1);

    window.location.href =
      `/phase-management?projectId=${projectId}&phaseId=${next?.id}`;

  };

  const handleMarkComplete = async () => {
    await phaseService?.updatePhaseProgress(phaseData?.id, 100);
  };

  const handleProgressUpdate = async (value) => {
    await phaseService?.updatePhaseProgress(phaseData?.id, value);
  };

  /* ---------------- RENDER ---------------- */

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin h-12 w-12 border-b-2 border-primary rounded-full"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center text-red-600">
        {error}
      </div>
    );
  }

  /* ---- AI PROJECT LAYOUT ---- */
  if (isAiProject) {
    return (
      <div className="min-h-screen bg-background">
        <PrimaryNavigation />
        <ContextualBreadcrumb />
        <div className="main-content-mobile-nav">
          <AIPhaseManagement
            project={project}
            phases={allPhases}
            projectName={projectName || project?.name}
            onBack={() => navigate('/boss-home-page')}
            onProjectStart={(updates) => setProject((prev) => ({ ...prev, ...updates }))}
          />
        </div>
        <FloatingActionButton userRole="boss" />
      </div>
    );
  }

  /* ---- MANUAL PROJECT LAYOUT ---- */

  const currentPhaseIndex =
    phases?.findIndex((p) => p?.id === phaseData?.id);

  const isFinalPhase =
    currentPhaseIndex !== -1 &&
    currentPhaseIndex === phases?.length - 1;

  return (
    <div className="min-h-screen bg-background">
      <PrimaryNavigation />
      <ContextualBreadcrumb />
      <div className="main-content-mobile-nav space-y-6">

        <PhaseHeader
          phase={phaseData}
          projectName={projectName}
          onBack={() => navigate('/boss-home-page')}
          isFuturePhase={phaseData?.progress === 0}
          previousPhaseProgress={currentPhaseIndex > 0 ? phases?.[currentPhaseIndex - 1]?.progress : 100}
          estimatedStartDate={phaseData?.planned_start_date}
        />

        <ProjectTimeline
          phases={phases}
          currentPhaseId={phaseData?.id}
        />

        {/* NORMAL FILLED SLIDER */}

        {!isThreeMusketeers &&
          phaseData?.progress > 0 &&
          phaseData?.progress < 100 && (

          <div className="bg-card rounded-lg border border-border p-6">

            <div className="flex items-center justify-between mb-3">

              <span className="text-sm font-medium">
                Completion Progress
              </span>

              <span className="text-2xl font-bold text-primary">
                {phaseData?.progress}%
              </span>

            </div>

            <input
              type="range"
              min="0"
              max="100"
              value={phaseData?.progress}

              style={{
                background: `linear-gradient(
                  to right,
                  #3b82f6 0%,
                  #3b82f6 ${phaseData?.progress}%,
                  #e5e7eb ${phaseData?.progress}%,
                  #e5e7eb 100%
                )`
              }}

              onChange={(e) => {
                const value = parseInt(e?.target?.value);
                setPhaseData(prev => ({
                  ...prev,
                  progress: value
                }));
              }}

              onMouseUp={(e) =>
                handleProgressChange(parseInt(e?.target?.value))
              }

              className="w-full h-3 rounded-lg appearance-none cursor-pointer"
            />

          </div>

        )}

        {/* 3 MUSKETEERS */}

        {isThreeMusketeers && phaseData?.progress < 100 && (

          <div className="bg-card rounded-lg border border-border p-6 space-y-6">

            <h3 className="text-lg font-semibold">
              3 Musketeers Progress
            </h3>

            {['plumber','electrician','aircon']?.map((trade) => (

              <div key={trade}>

                <div className="flex justify-between mb-2">
                  <span className="capitalize font-medium">
                    {trade}
                  </span>
                  <span className="font-semibold">
                    {local3M?.[trade]}%
                  </span>
                </div>

                <input
                  type="range"
                  min="0"
                  max="100"
                  value={local3M?.[trade]}

                  style={{
                    background: `linear-gradient(
                      to right,
                      #3b82f6 0%,
                      #3b82f6 ${local3M?.[trade]}%,
                      #e5e7eb ${local3M?.[trade]}%,
                      #e5e7eb 100%
                    )`
                  }}

                  onChange={(e) =>
                    handle3MChange(
                      trade,
                      parseInt(e?.target?.value)
                    )
                  }

                  onMouseUp={commit3MProgress}

                  className="w-full h-3 rounded-lg appearance-none cursor-pointer"
                />

              </div>

            ))}

            <div className="pt-4 border-t border-border">

              <div className="flex justify-between text-sm">

                <span className="font-medium">
                  Overall Progress
                </span>

                <span className="font-bold text-primary">
                  {computedAverage}%
                </span>

              </div>

            </div>

          </div>

        )}

        {/* REMARKS */}

        {remarks?.length > 0 && (

          <div className="bg-card rounded-lg border border-border p-6">

            <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
              <Icon name="MessageSquare" size={18} />
              Worker Remarks
            </h3>

            {remarks?.map((remark) => (

              <div key={remark?.id} className="mb-3 text-sm">
                {remark?.remark_text}
              </div>

            ))}

          </div>

        )}

        <TimelineComparison
          suggestedStart={phaseData?.planned_start_date}
          suggestedEnd={phaseData?.planned_end_date}
          actualStart={phaseData?.actual_start_date}
          actualEnd={phaseData?.actual_end_date}
          status={phaseData?.status}
          progress={phaseData?.progress}
          onUpdateActual={async (field, value) => {
            await phaseService?.updatePhaseField(phaseData?.id, field, value);
          }}
          userRole="boss"
        />

        <PhaseActions
          phase={phaseData}
          userRole="boss"
          onMarkComplete={handleMarkComplete}
          onAdvancePhase={handleAdvancePhase}
          onOverride={handleOverride}
          isFinalPhase={isFinalPhase}
          onProgressUpdate={handleProgressUpdate}
          allPhases={allPhases}
          projectLifecycleStatus={projectLifecycleStatus}
        />

      </div>
      <FloatingActionButton userRole="boss" />
    </div>
  );

};

export default PhaseManagement;